<template>
    <div class="d-flex flex-column overflow-hidden h-100">
        <vue-custom-scrollbar
            class="app-content-padding flex-grow-1 overflow-hidden h-100"
        >
            <ts-page-title
                :title="$t('cashTransfer.pageTitle')"
                :breadcrumb="[
                    { text: $t('home'), href: '/' },
                    {
                        text: $t('cashTransfer.pageTitle'),
                        href: '/admin/cash-transfers'
                    },
                    { text: $t('edit'), active: true }
                ]"
            />
            <div class="demo-spin-article">
                <ts-panel>
                    <ts-panel-wrapper>
                        <div class="tw-mb-4">
                            <span
                                class="badge bg-secondary rounded-pill tw-text-sm"
                            >
                                {{ $t("cashTransfer.transferNo") }}:
                                <strong>{{ model.transfer_no }}</strong>
                            </span>
                        </div>
                        <form-input
                            ref="formInput"
                            v-model="model"
                            :validate="errors"
                            :transfer-by-name="transferByName"
                            :currencies="currencies"
                            :banks="banks"
                            :remark-options="remarkOptions"
                            @remark-created="onRemarkCreated"
                        />
                    </ts-panel-wrapper>
                </ts-panel>
            </div>
        </vue-custom-scrollbar>

        <Spin size="large" fix v-if="loading"></Spin>

        <div
            id="footer"
            class="app-footer m-0 tw-flex tw-justify-end tw-space-x-2"
            style="background-color: #e9ecef"
        >
            <ts-button
                @click.prevent="$router.push({ name: 'cash-transfer' })"
                class="btn-gray"
            >
                {{ $t("cancel") }}
            </ts-button>
            <ts-button
                color="primary"
                :waiting="waiting"
                :disabled="waiting"
                @click.prevent="onSave"
            >
                {{ $t("save") }}
            </ts-button>
        </div>
    </div>
</template>

<script>
import AppOptions from "@/config/AppOptions.vue";
import { Errors } from "form-backend-validation";
import { mapActions, mapState } from "vuex";
import { isEmpty } from "lodash";
import FormInput from "./components/form-input.vue";
import moment from "moment";

export default {
    name: "CashTransferEdit",
    components: { FormInput },
    data() {
        return {
            loading: false,
            waiting: false,
            errors: new Errors(),
            model: {
                transfer_id: null,
                transfer_no: null,
                transfer_by: null,
                transfer_date: null,
                transfer_amount: null,
                currency_id: null,
                bank_id: null,
                bank_account_no: "",
                bank_account_name: "",
                remark: "",
                bank_slip_photos: [],
                bank_slip_date: null,
                bank_slip_remark: ""
            },
            remarkOptions: [
                "Head Office Charge",
                "Office Supply",
                "Cash Out to HOF",
                "Oil Expense",
                "Other"
            ]
        };
    },
    created() {
        AppOptions.appContentFullHeight = true;
        AppOptions.appContentClass = "p-0";
    },
    computed: {
        ...mapState("finance/cashTransfer", [
            "edit_data",
            "currencies",
            "banks"
        ]),
        transferByName() {
            if (this.edit_data && this.edit_data.transfer_by) {
                return this.edit_data.transfer_by.employee_name_en;
            }
            const user = this.$store.state.authUser.user;
            return user && user.employee
                ? user.employee.employee_name_en
                : user.user_name;
        }
    },
    methods: {
        ...mapActions("finance/cashTransfer", [
            "find",
            "getCurrencies",
            "getBanks"
        ]),
        async fetchFormData() {
            this.loading = true;
            await this.getCurrencies();
            await this.getBanks();
            await this.find(this.$route.params.id);
            this.setEditData();
            this.loading = false;
        },
        setEditData() {
            if (!isEmpty(this.edit_data)) {
                this.model = {
                    transfer_id: this.edit_data.transfer_id,
                    transfer_no: this.edit_data.transfer_no,
                    transfer_by: this.edit_data.transfer_by
                        ? this.edit_data.transfer_by.employee_id
                        : null,
                    transfer_date: this.edit_data.transfer_date
                        ? moment(
                              this.edit_data.transfer_date,
                              ["DD-MM-YYYY", "YYYY-MM-DD"]
                          ).format("DD-MM-YYYY")
                        : null,
                    transfer_amount: this.edit_data.transfer_amount,
                    currency_id: this.edit_data.currency_id,
                    bank_id: this.edit_data.bank_id,
                    bank_account_no: this.edit_data.bank_account_no || "",
                    bank_account_name: this.edit_data.bank_account_name || "",
                    remark: this.edit_data.remark || "",
                    bank_slip_photos: Array.isArray(this.edit_data.bank_slip_photos) && this.edit_data.bank_slip_photos.length
                        ? this.edit_data.bank_slip_photos.slice()
                        : (this.edit_data.bank_slip_photo ? [this.edit_data.bank_slip_photo] : []),
                    bank_slip_date: this.edit_data.bank_slip_date
                        ? moment(
                              this.edit_data.bank_slip_date,
                              ["DD-MM-YYYY", "YYYY-MM-DD"]
                          ).format("DD-MM-YYYY")
                        : null,
                    bank_slip_remark: this.edit_data.bank_slip_remark || ""
                };

                // ensure a previously saved custom remark (one not in the
                // default list) still shows up as a selectable option
                if (
                    this.model.remark &&
                    !this.remarkOptions.includes(this.model.remark)
                ) {
                    this.remarkOptions.push(this.model.remark);
                }
            }
        },
        onRemarkCreated(val) {
            this.remarkOptions.push(val);
        },
        onSave() {
            if (this.$refs.formInput)
                this.$refs.formInput.commitPendingRemark();
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("finance/cashTransfer/update", {
                    id: this.model.transfer_id,
                    data: this.model
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    if (response.message_id == 0) {
                        this.$store.commit("finance/cashTransfer/RESET_STATE");
                        this.$router.push({ name: "cash-transfer" });
                    }
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.waiting = false));
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "CASH TRANSFER",
                desc: not.text
            });
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.fetchFormData());
    },
    beforeRouteLeave(to, from, next) {
        AppOptions.appContentFullHeight = false;
        AppOptions.appContentClass = "";
        this.$store.commit("finance/cashTransfer/RESET_STATE");
        next();
    }
};
</script>
